import React, { useState } from 'react';

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import {
  Box,
  ClickAwayListener,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Popper,
  Stack,
  Switch,
  Tooltip
} from '@mui/material';

import SortableField from './SortableField';
import { classes } from './styles';
import { clearPreferenceCache } from '../../../helpers/uiPreferencesHelpers';
import { handleAPIRequest } from '../../Utilities/handleAPIRequest';

import type { DragEndEvent } from '@dnd-kit/core';
import type { ChangeEvent, Dispatch, MouseEvent } from 'react';

import type {
  GenericTableData,
  TableColumnAction,
  TableDensity,
  TSortableColumn,
  TTableState
} from '../types';

interface IColumnSettings {
  dispatch: Dispatch<TableColumnAction<TSortableColumn[]>>;
  tableState: TTableState<GenericTableData, TSortableColumn[]>;
  label: string;
  userPreferenceSource?: string;
  disableDensity?: boolean;
}

const DENSITIES = ['Default', 'Compact', 'Ultra Compact'];

const ColumnSettings = ({
  dispatch,
  tableState,
  label,
  userPreferenceSource,
  disableDensity
}: IColumnSettings) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleIconClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setIsOpen((prev) => !prev);
  };

  const sendLocalUserPreferences = (newColumns: TSortableColumn[]) => {
    if (userPreferenceSource) {
      handleAPIRequest({
        url: `/api/v4/ui_preferences`,
        method: 'POST',
        body: {
          ui_preference: {
            sortable_columns: newColumns,
            source: userPreferenceSource
          }
        }
      });
      clearPreferenceCache(userPreferenceSource);
    }
  };

  const handleFieldSwitchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newSortableColumns = tableState.columns.map((field) => {
      if (field.id === event.target.name) {
        return { ...field, enabled: event.target.checked };
      }
      return field;
    });
    dispatch({ type: 'SET_COLUMNS', payload: newSortableColumns });
    sendLocalUserPreferences(newSortableColumns);
  };

  const handleDensitySwitchChange = (newDensity: TableDensity) => {
    dispatch({ type: 'SET_DENSITY', payload: newDensity });
    handleAPIRequest({
      url: '/api/v4/ui_preferences',
      method: 'POST',
      body: {
        ui_preference: {
          columns: {
            density: newDensity
          },
          source: 'universal'
        }
      }
    });
    clearPreferenceCache('universal');
  };

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = tableState.columns.map((c) => c.id).indexOf(String(active.id));
      const newIndex = tableState.columns.map((c) => c.id).indexOf(String(over?.id));
      const newSortableColumns = arrayMove(tableState.columns, oldIndex, newIndex);

      dispatch({ type: 'SET_COLUMNS', payload: newSortableColumns });
      sendLocalUserPreferences(newSortableColumns);
    }
  }

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <Box>
        <Tooltip arrow placement="top" title="Table settings">
          <IconButton
            id={`field-dropdown-${label}-button`}
            onClick={handleIconClick}
            sx={isOpen ? { ...classes.iconButton, ...classes.iconButtonOpen } : classes.iconButton}
          >
            <ViewColumnIcon />
          </IconButton>
        </Tooltip>
        <Popper
          anchorEl={anchorEl}
          id="column-settings-popover"
          open={isOpen}
          placement="bottom-end"
          sx={classes.popover}
        >
          <Box sx={classes.popoverContent}>
            <FormControl component="fieldset" variant="standard">
              <Stack sx={classes.tableSettingsHeader}>Column</Stack>
              <FormGroup id="column-settings-switches">
                <DndContext
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                  sensors={sensors}
                >
                  <SortableContext
                    items={tableState.columns}
                    strategy={verticalListSortingStrategy}
                  >
                    {tableState.columns.map(
                      (field, index) =>
                        field.canToggle && (
                          <SortableField
                            key={index}
                            field={field}
                            handleFieldSwitchChange={handleFieldSwitchChange}
                          />
                        )
                    )}
                  </SortableContext>
                </DndContext>
              </FormGroup>
            </FormControl>
            {!disableDensity && (
              <Stack sx={{ ...classes.tableSettingsHeader, paddingTop: 2 }}>Density</Stack>
            )}
            {!disableDensity && (
              <FormControl>
                <FormGroup>
                  {DENSITIES.map((density, index) => {
                    const isSwitched = density === tableState.density;
                    return (
                      <FormControlLabel
                        key={index}
                        label={density}
                        labelPlacement="start"
                        control={
                          <Switch
                            checked={isSwitched}
                            onChange={() => handleDensitySwitchChange(density as TableDensity)}
                            sx={isSwitched ? classes.switchActive : classes.switch}
                          />
                        }
                        sx={
                          !isSwitched
                            ? { ...classes.switchLabels, ...classes.labelActive }
                            : classes.switchLabels
                        }
                      />
                    );
                  })}
                </FormGroup>
              </FormControl>
            )}
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default ColumnSettings;
